'use client';
import React, { memo, useEffect, useState } from 'react';

import classNames from 'classnames';
import { Select, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';

import { SelectProps } from 'antd/es/select';
import { useSearchParams } from 'next/navigation';
import { CHAT_ID } from '@whelk/const/src/routerUrl';
import useChatModel from '@whelk/hooks/src/useChatModel';
import { ChatModel } from '@whelk/services/src/type/chat';

import ModelLabel from '../ModelLabel';
import ModelOption from '../ModelOption';
import { CaretDown } from './icon';

import './index.scss';

const ModelSelect = memo(({ client }: { client: string }) => {
  const { t } = useTranslation();
  const { model, modelOptions, setModel, closeModelTip, isShowModelTip } = useChatModel();
  const [isOpen, setIsOpen] = useState(false);

  const searchParams = useSearchParams();
  const selectedChatID = searchParams?.get(CHAT_ID) || '';
  const isChatPage = !!selectedChatID;

  const handleSetModel = (value: string) => {
    setModel(value);
  };

  // 展示后关闭提示
  useEffect(() => {
    return () => {
      if (isShowModelTip) {
        closeModelTip();
      }
    };
  }, []);

  if (!modelOptions?.length) {
    return null;
  }
  const selectProps: SelectProps = {
    className: classNames('model-select hover:bg-col_bg03 text-col_text00'),
    placement: 'bottomLeft',
    open: isShowModelTip ? true : undefined,
    value: model,
    options: modelOptions,
    onChange: handleSetModel,
    onClick: () => {
      closeModelTip();
    },
    virtual: false,
    optionRender: ({ data, value }) => {
      const { description, img, value: optionValue, label } = data as ChatModel;
      return (
        <ModelOption
          value={String(optionValue || '')}
          label={label}
          description={description}
          img={img}
          isSelected={value === model}
        />
      );
    },
    suffixIcon: (
      <div
        className={classNames('transition-transform duration-200', {
          'rotate-180': isOpen,
          'text-col_text03': isChatPage,
        })}
      >
        <CaretDown />
      </div>
    ),
    labelInValue: false,
    ...({
      labelRender: () => {
        return <ModelLabel />;
      },
    } as any),
    popupClassName: 'model-select-dropdown',
    disabled: isChatPage,
    onDropdownVisibleChange: (open) => {
      setIsOpen(open);
    },
  };

  return (
    <Tooltip
      overlayClassName={classNames('model-switch-tooltip', {
        'md:hidden': client === 'mobile',
      })}
      placement='bottomLeft'
      arrow={false}
      open={isChatPage ? undefined : false}
      title={t('chat_model_switch_disabled')}
      zIndex={1040}
    >
      <Select {...selectProps} />
    </Tooltip>
  );
});

ModelSelect.displayName = 'ModelSelect';

export { ModelSelect };
